import React, { type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { variables } from '@splunk/themes';
import {
    FOOTER_AREA,
    type IntrinsicAttributes,
    type ShowBorderProp,
} from './constants';

/**
 * Common footer container.\
 * This is only needed if the footer content is custom (**not** a set of buttons).
 *
 * @remarks
 * This is named `SidebarFooter` instead of `ContentFooter` because it applies to the whole `<aside />` DOM structure _not_ just the content
 */
export const SidebarFooter = styled.footer.attrs<IntrinsicAttributes>(
    (props) => ({ 'data-test': props['data-test'] ?? 'sidebar-footer' })
)<ShowBorderProp>`
    grid-area: ${FOOTER_AREA};

    ${(props) =>
        props.$showBorder &&
        css`
            border-top: 1px solid ${variables.borderColorStrong};
        `}

    display: flex;
    flex-direction: column;
`;

/**
 * Container for a row of equally spaced and sized buttons
 *
 * This is only needed if the footer content has both a button row and something else, which necessitates direct use such as:
 * @example
 * ```jsx
 * <SidebarFooter>
 *     <InteractionFieldSelector />
 *     <ButtonRow>
 *         <button type="button">Cancel</button>
 *         <button type="button">Apply</button>
 *     </ButtonRow>
 * </SidebarFooter>
 * ```
 */
export const ButtonRow = styled.div`
    flex-basis: auto;
    display: flex;
    justify-content: space-around;
    padding: ${variables.spacingMedium} ${variables.spacingLarge};
    gap: ${variables.spacingLarge};
`;

/** Content footer with only button content */
export const SidebarFooterButtons = ({
    children,
    ...props
}: {
    children?: ReactNode;
} & Partial<IntrinsicAttributes>) => (
    <SidebarFooter {...props}>
        <ButtonRow>{children}</ButtonRow>
    </SidebarFooter>
);
